// ============================================================
// MANIC MEETER — English content: roles, departments, meetings
// ============================================================
// The office is still in Germany. Job titles are translated because you read
// them every turn; the local institutions that carry the joke are not.

import { ColleagueRole, Department, MeetingType, PlayerRole } from "../../types";
import type { MeetingTypeContent } from "../contract";

export const DEPARTMENTS: Record<Department, string> = {
  [Department.Frontend]: "Frontend",
  [Department.Backend]: "Backend",
  [Department.Client]: "Client",
  [Department.HR]: "HR",
  [Department.Marketing]: "Marketing",
  [Department.Management]: "Management",
};

export const COLLEAGUE_ROLES: Record<ColleagueRole, string> = {
  [ColleagueRole.FrontendDev]: "Frontend Developer",
  [ColleagueRole.BackendDev]: "Backend Developer",
  [ColleagueRole.ClientManager]: "Client Manager",
  [ColleagueRole.HRManager]: "HR Manager",
  [ColleagueRole.MarketingGuru]: "Marketing Guru",
  [ColleagueRole.Chef]: "The Boss",
  [ColleagueRole.Abteilungsleiter]: "Head of Department",
  [ColleagueRole.Teamleiter]: "Team Lead",
  [ColleagueRole.Designer]: "UX Designer",
  [ColleagueRole.Praktikant]: "Intern",
  [ColleagueRole.ScrumMaster]: "Scrum Master",
  [ColleagueRole.ProductOwner]: "Product Owner",
  [ColleagueRole.Sprachbarriere]: "Pre-English",
};

export const PLAYER_ROLES: Record<PlayerRole, string> = {
  [PlayerRole.Praktikant]: "Intern",
  [PlayerRole.Azubi]: "Apprentice",
  [PlayerRole.Werkstudent]: "Working Student",
  [PlayerRole.JuniorDev]: "Junior Dev",
};

export const MEETING_TYPES: Record<MeetingType, MeetingTypeContent> = {
  [MeetingType.DailyStandup]: {
    name: "Daily Standup",
    description: "15 minutes that feel like two hours.",
  },
  [MeetingType.Planning]: {
    name: "Sprint Planning",
    description: "Sprint Planning: where estimates are born in order to die.",
  },
  [MeetingType.LunchMeeting]: {
    name: "Lunch Meeting",
    description: "Your lunch goes cold while somebody talks about KPIs.",
  },
  [MeetingType.OneOnOne]: {
    name: "1-on-1",
    description: "Just you and your team lead. No exit.",
  },
  [MeetingType.Scrum]: {
    name: "Scrum Meeting",
    description: "Agile™ in its purest form. Still somehow feels like waterfall.",
  },
  [MeetingType.Schulung]: {
    name: "Mandatory Training",
    description: "Compulsory session: 'Compliance in the Digital Age'. Attendance is logged.",
  },
  [MeetingType.Retro]: {
    name: "Retrospective",
    description: "What went well? What went badly? (Everything.)",
  },
  [MeetingType.AllHands]: {
    name: "All-Hands",
    description: "The boss talks. Everyone nods. Nobody listens.",
  },
  [MeetingType.Brainstorming]: {
    name: "Brainstorming Session",
    description: "Post-its, whiteboards and zero outcomes.",
  },
  [MeetingType.StatusUpdate]: {
    name: "Status Update",
    description: "Everything that could have been an email.",
  },
  [MeetingType.Vorstandsmeeting]: {
    name: "Board Meeting",
    description: "The board has questions. Many questions. And you have no answers.",
  },
};

// ---- ASCII scene above each meeting ----
// Box widths are load-bearing: every replacement keeps the original length.

export const MEETING_SCENES: Record<MeetingType, string> = {
  [MeetingType.DailyStandup]: `<pre class="ascii-scene">
+-------------------+
| DAILY STANDUP  .. |
|  +-----------+    |
|  | Kanban    |    |
|  | ####::::: |    |
|  +-----------+    |
+-------------------+</pre>`,
  [MeetingType.Planning]: `<pre class="ascii-scene">
+-------------------+
|  SPRINT PLANNING  |
|  +--+--+--+--+    |
|  |S1|S2|S3|S4|    |
|  +--+--+--+--+    |
|  |13| 8| 5|21|    |
|  +--+--+--+--+    |
|  Story Points     |
+-------------------+</pre>`,
  [MeetingType.LunchMeeting]: `<pre class="ascii-scene">
+-------------------+
|  LUNCH MEETING    |
|  +-----------+    |
|  | ~~~~~~~~~ |    |
|  |  Kantine  |    |
|  +-----------+    |
+-------------------+</pre>`,
  [MeetingType.OneOnOne]: `<pre class="ascii-scene">
+-------------------+
|     1-ON-1        |
|                   |
|   o      o        |
|  /|\    /|\       |
|  / \    / \       |
|  [__]   [__]      |
+-------------------+</pre>`,
  [MeetingType.Scrum]: `<pre class="ascii-scene">
+-------------------+
|  SCRUM MEETING    |
|  +===========+    |
|  | Sprint ## |    |
|  | Velocity^ |    |
|  +===========+    |
|  ~ Iteration ~    |
+-------------------+</pre>`,
  [MeetingType.Schulung]: `<pre class="ascii-scene">
+-------------------+
|  TRAINING         |
|  +============+   |
|  | Compliance |   |
|  | ########## |   |
|  | Slide 3/47 |   |
|  +============+   |
+-------------------+</pre>`,
  [MeetingType.Retro]: `<pre class="ascii-scene">
+-------------------+
|  RETROSPECTIVE    |
|  +--+ +--+ +--+   |
|  |:)| |:-| |:(|   |
|  +--+ +--+ +--+   |
|  Post-Its         |
|  =============    |
+-------------------+</pre>`,
  [MeetingType.AllHands]: `<pre class="ascii-scene">
+-------------------+
|   ALL-HANDS       |
|   +==========+    |
|   | ##  /\   |    |
|   | ## /  \  |    |
|   +==========+    |
|  ooooooo ooooooo  |
+-------------------+</pre>`,
  [MeetingType.Brainstorming]: `<pre class="ascii-scene">
+-------------------+
|  BRAINSTORMING *  |
|  +------------+   |
|  | ## ## ## ## |   |
|  | Whiteboard  |   |
|  | ## ## ## ## |   |
|  +------------+   |
+-------------------+</pre>`,
  [MeetingType.StatusUpdate]: `<pre class="ascii-scene">
+-------------------+
|  STATUS UPDATE    |
|  > --------       |
|  > --------       |
|  > --------       |
|  +===========+    |
|  | [x][x][ ] |    |
|  +===========+    |
+-------------------+</pre>`,
  [MeetingType.Vorstandsmeeting]: `<pre class="ascii-scene ascii-scene-boss">
+===================+
|  BOARD MEETING    |
|  ===============  |
|  +-------------+  |
|  | Q3 NUMBERS  |  |
|  | STRATEGY  ^ |  |
|  +-------------+  |
|  !! CAUTION !!    |
+===================+</pre>`,
};
